import { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { MapPin, Phone, Mail, ArrowRight, Star, Instagram, Facebook, Upload, Check, Sparkles, ChevronRight, ChevronLeft, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Premium Visualizer Widget Component - Multi-step Brand → Finish → Color
const PremiumVisualizerWidget = ({ studioId, slug, studioDetails, accentColor = '#FF3399' }) => {
  const [step, setStep] = useState(1);
  const [wrapSelectionStep, setWrapSelectionStep] = useState(1); // 1: Brand, 2: Finish, 3: Color
  const [carImage, setCarImage] = useState(null);
  const [carImageBase64, setCarImageBase64] = useState('');
  const [masterWraps, setMasterWraps] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedFinish, setSelectedFinish] = useState(null);
  const [selectedWrap, setSelectedWrap] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [resultImage, setResultImage] = useState(null);
  const [sampleCars, setSampleCars] = useState([]);
  const [showInquireModal, setShowInquireModal] = useState(false);
  const fileInputRef = useRef(null);

  // Available finishes
  const finishTypes = ['Gloss', 'Matte', 'Satin', 'Metallic', 'Brushed', 'Chrome', 'Carbon'];

  // Premium sample cars - BMW, Porsche, Lamborghini
  const premiumSampleCars = [
    { name: 'BMW M Series', url: 'https://customer-assets.emergentagent.com/job_vehicle-wrap-1/artifacts/nqdocdhw_C5CA1264-462B-40C8-BC65-0CF63342087C%202.jpg' },
    { name: 'Porsche 911', url: 'https://images.unsplash.com/photo-1693164526787-4963200aba01?w=800&q=80' },
    { name: 'Lamborghini', url: 'https://images.unsplash.com/photo-1532581140115-3e355d1ed1de?w=800&q=80' },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch master catalog wraps (from Folienpartner24)
        const masterRes = await axios.get(`${API}/wraps/master-catalog`);
        setMasterWraps(masterRes.data);
        
        // Fetch manufacturers with counts
        const mfrRes = await axios.get(`${API}/wraps/manufacturers`);
        // Filter out manufacturers with 0 wraps
        const activeMfrs = mfrRes.data.manufacturers.filter(m => m.wrap_count > 0);
        setManufacturers(activeMfrs);
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
    setSampleCars(premiumSampleCars);
  }, []);

  // Get available finishes for selected brand
  const getAvailableFinishes = () => {
    if (!selectedBrand) return [];
    const brandWraps = masterWraps.filter(w => w.manufacturer === selectedBrand);
    const finishes = [...new Set(brandWraps.map(w => w.finish))];
    return finishTypes.filter(f => finishes.includes(f));
  };

  // Get wraps filtered by brand and finish
  const getFilteredWraps = () => {
    if (!selectedBrand || !selectedFinish) return [];
    return masterWraps.filter(w => 
      w.manufacturer === selectedBrand && 
      w.finish === selectedFinish
    );
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      setCarImage(event.target.result);
      setCarImageBase64(event.target.result);
      setStep(2);
    };
    reader.readAsDataURL(file);
  };

  const handleSampleCarSelect = async (car) => {
    try {
      const response = await fetch(car.url);
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onload = (event) => {
        setCarImage(event.target.result);
        setCarImageBase64(event.target.result);
        setStep(2);
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      setCarImage(car.url);
      setCarImageBase64(car.url);
      setStep(2);
    }
  };

  // Handle brand selection
  const handleBrandSelect = (brand) => {
    setSelectedBrand(brand);
    setSelectedFinish(null);
    setWrapSelectionStep(2);
  };

  // Handle finish selection
  const handleFinishSelect = (finish) => {
    setSelectedFinish(finish);
    setWrapSelectionStep(3);
  };

  // Go back in wrap selection
  const handleWrapSelectionBack = () => {
    if (wrapSelectionStep === 3) {
      setSelectedFinish(null);
      setWrapSelectionStep(2);
    } else if (wrapSelectionStep === 2) {
      setSelectedBrand(null);
      setWrapSelectionStep(1);
    }
  };

  const handleWrapSelect = async (wrap) => {
    setSelectedWrap(wrap);
    setGenerating(true);

    try {
      const renderRes = await axios.post(`${API}/renders`, {
        studio_id: studioId,
        wrap_id: wrap.id,
        car_image_base64: carImageBase64,
        finish: wrap.finish || 'gloss'
      });

      const renderId = renderRes.data.id;
      await axios.post(`${API}/renders/${renderId}/generate`);

      let attempts = 0;
      const maxAttempts = 60;
      const pollInterval = setInterval(async () => {
        attempts++;
        try {
          const statusRes = await axios.get(`${API}/renders/${renderId}`);
          if (statusRes.data.status === 'completed' && statusRes.data.result_image_url) {
            clearInterval(pollInterval);
            setResultImage(statusRes.data.result_image_url);
            setGenerating(false);
            setStep(3);
          } else if (statusRes.data.status === 'fallback' || attempts >= maxAttempts) {
            clearInterval(pollInterval);
            setGenerating(false);
            toast.error('Generation failed. Please try again.');
          }
        } catch (err) {
          clearInterval(pollInterval);
          setGenerating(false);
          toast.error('Something went wrong');
        }
      }, 2000);
    } catch (err) {
      setGenerating(false);
      toast.error('Failed to start generation');
    }
  };

  const resetVisualizer = () => {
    setStep(1);
    setWrapSelectionStep(1);
    setCarImage(null);
    setCarImageBase64('');
    setSelectedWrap(null);
    setSelectedBrand(null);
    setSelectedFinish(null);
    setResultImage(null);
  };

  return (
    <section className="relative pt-20 pb-12 overflow-hidden" id="visualizer">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-zinc-900/50 to-[#050505]" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full blur-3xl opacity-30"
           style={{ background: `radial-gradient(ellipse, ${accentColor}20 0%, transparent 70%)` }} />
      
      <div className="max-w-6xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-800 mb-4">
            <Sparkles className="w-4 h-4" style={{ color: accentColor }} />
            <span className="font-mono text-xs tracking-widest uppercase text-zinc-400">AI Wrap Visualizer</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-white mb-2">
            See your wrap <span style={{ color: accentColor }}>before you buy</span>
          </h2>
          <p className="text-zinc-500 max-w-md mx-auto text-sm">
            Upload a photo of your car and preview different wrap colors instantly.
          </p>
        </div>

        {/* Visualizer Container */}
        <div className="relative">
          <div className="absolute -inset-1 rounded-none opacity-50 blur-sm"
               style={{ background: `linear-gradient(135deg, ${accentColor}40 0%, transparent 50%, ${accentColor}40 100%)` }} />
          
          <div className="relative bg-[#09090B] border border-zinc-800 p-6">
            {/* Step Indicator */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-zinc-800">
              <div className="flex items-center gap-3">
                {[1, 2, 3].map((s) => (
                  <div key={s} className="flex items-center gap-2">
                    <div 
                      className={`w-8 h-8 flex items-center justify-center font-mono text-xs border-2 transition-all duration-300
                        ${step > s 
                          ? 'border-transparent text-black' 
                          : step === s 
                            ? 'border-current shadow-lg shadow-current/30' 
                            : 'border-zinc-700 text-zinc-600'
                        }`}
                      style={{ 
                        backgroundColor: step > s ? accentColor : 'transparent',
                        color: step === s ? accentColor : undefined,
                        borderColor: step === s ? accentColor : undefined
                      }}
                    >
                      {step > s ? <Check className="w-4 h-4" /> : s}
                    </div>
                    {s < 3 && <div className={`w-6 h-px ${step > s ? 'bg-gradient-to-r from-current to-transparent' : 'bg-zinc-800'}`} 
                                  style={{ color: step > s ? accentColor : undefined }} />}
                  </div>
                ))}
              </div>
              <div className="font-mono text-xs tracking-widest text-zinc-500">
                {step === 1 ? 'UPLOAD' : step === 2 ? 'SELECT' : 'PREVIEW'}
              </div>
            </div>

            {/* Step 1: Upload */}
            {step === 1 && (
              <div className="text-center py-6">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border border-dashed border-zinc-700 p-12 cursor-pointer hover:border-current transition-all group"
                  style={{ '--hover-color': accentColor }}
                >
                  <div className="w-14 h-14 mx-auto mb-4 border border-zinc-700 flex items-center justify-center group-hover:border-current transition-colors"
                       style={{ color: accentColor }}>
                    <Upload className="w-7 h-7 text-zinc-600 group-hover:text-current transition-colors" />
                  </div>
                  <p className="text-zinc-300 font-medium mb-1">Drop your car photo here</p>
                  <p className="text-xs text-zinc-600">or click to browse • PNG, JPG up to 10MB</p>
                </div>
                
                {sampleCars.length > 0 && (
                  <div className="mt-6">
                    <p className="font-mono text-xs tracking-widest text-zinc-600 mb-3">OR TRY A SAMPLE</p>
                    <div className="flex justify-center gap-3">
                      {sampleCars.map((car, i) => (
                        <div
                          key={i}
                          onClick={() => handleSampleCarSelect(car)}
                          className="relative cursor-pointer group"
                        >
                          <img
                            src={car.url}
                            alt={car.name}
                            className="w-20 h-14 object-cover border border-zinc-800 group-hover:border-current transition-all"
                            style={{ '--hover-color': accentColor }}
                          />
                          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                               style={{ background: `${accentColor}20` }}>
                            <span className="text-[10px] font-medium text-white">Select</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Select Wrap - Multi-step: Brand → Finish → Color */}
            {step === 2 && !generating && (
              <div className="flex gap-6">
                {/* Left: Car preview - full visibility */}
                <div className="w-1/2 flex-shrink-0">
                  <p className="font-mono text-xs tracking-widest text-zinc-500 mb-2">YOUR VEHICLE</p>
                  <div className="relative">
                    <img 
                      src={carImage} 
                      alt="Your car" 
                      className="w-full h-[280px] object-cover border border-zinc-800"
                    />
                    {/* Selection breadcrumb */}
                    <div className="absolute bottom-2 left-2 flex items-center gap-1 text-xs">
                      {selectedBrand && (
                        <span className="px-2 py-0.5 border" style={{ backgroundColor: `${accentColor}20`, borderColor: `${accentColor}50`, color: accentColor }}>
                          {selectedBrand}
                        </span>
                      )}
                      {selectedFinish && (
                        <>
                          <ChevronRight className="w-3 h-3 text-zinc-600" />
                          <span className="bg-zinc-800 text-zinc-300 px-2 py-0.5 border border-zinc-700">
                            {selectedFinish}
                          </span>
                        </>
                      )}
                    </div>
                    <button 
                      onClick={resetVisualizer}
                      className="absolute top-2 left-2 text-xs bg-black/60 text-zinc-400 hover:text-white px-2 py-1 flex items-center gap-1"
                    >
                      <ChevronLeft className="w-3 h-3" /> Change
                    </button>
                  </div>
                </div>

                {/* Right: Selection panel */}
                <div className="w-1/2 flex flex-col">
                  {/* Quick Pick - Popular wraps */}
                  {wrapSelectionStep === 1 && masterWraps.length > 0 && (
                    <div className="mb-3">
                      <p className="font-mono text-xs mb-2" style={{ color: accentColor }}>🔥 QUICK PICK</p>
                      <div className="flex gap-2 overflow-x-auto pb-4">
                        {masterWraps.slice(0, 8).map((wrap) => (
                          <button
                            key={wrap.id}
                            onClick={() => handleWrapSelect(wrap)}
                            data-name={wrap.name}
                            className="wrap-swatch flex-shrink-0 w-10 h-10 border-2 border-zinc-700 overflow-hidden"
                          >
                            <img src={wrap.image_url} alt={wrap.name} className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step 2.1: Select Brand */}
                  {wrapSelectionStep === 1 && (
                    <div className="flex-1">
                      <p className="font-mono text-xs tracking-widest text-zinc-500 mb-2">1. SELECT BRAND</p>
                      <div className="grid grid-cols-2 gap-1.5">
                        {manufacturers.map((mfr) => (
                          <button
                            key={mfr.name}
                            onClick={() => handleBrandSelect(mfr.name)}
                            className="p-2.5 border border-zinc-800 hover:border-current transition-colors text-left group"
                            style={{ '--hover-color': accentColor }}
                          >
                            <span className="text-sm font-bold text-white group-hover:text-current transition-colors">
                              {mfr.name}
                            </span>
                            <span className="text-xs text-zinc-600 ml-1.5">
                              {mfr.wrap_count}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step 2.2: Select Finish */}
                  {wrapSelectionStep === 2 && (
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-mono text-xs tracking-widest text-zinc-500">2. SELECT FINISH</p>
                        <button 
                          onClick={handleWrapSelectionBack}
                          className="text-xs text-zinc-500 hover:text-current flex items-center gap-1"
                          style={{ '--hover-color': accentColor }}
                        >
                          <ChevronLeft className="w-3 h-3" /> Back
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-1.5">
                        {getAvailableFinishes().map((finish) => (
                          <button
                            key={finish}
                            onClick={() => handleFinishSelect(finish)}
                            className="p-2.5 border border-zinc-800 hover:border-current transition-colors text-center group"
                            style={{ '--hover-color': accentColor }}
                          >
                            <span className="text-sm font-medium text-white group-hover:text-current transition-colors">
                              {finish}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step 2.3: Select Color */}
                  {wrapSelectionStep === 3 && (
                    <div className="flex-1 flex flex-col">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-mono text-xs tracking-widest text-zinc-500">3. SELECT COLOR</p>
                        <button 
                          onClick={handleWrapSelectionBack}
                          className="text-xs text-zinc-500 hover:text-current flex items-center gap-1"
                          style={{ '--hover-color': accentColor }}
                        >
                          <ChevronLeft className="w-3 h-3" /> Back
                        </button>
                      </div>
                      <div className="grid grid-cols-4 gap-2 overflow-y-auto flex-1 max-h-[200px] pr-1 pt-6">
                        {getFilteredWraps().map((wrap) => (
                          <button
                            key={wrap.id}
                            onClick={() => handleWrapSelect(wrap)}
                            data-name={wrap.name.replace(selectedBrand || '', '').trim()}
                            className="wrap-swatch relative aspect-square border-2 border-zinc-800 overflow-hidden"
                          >
                            {wrap.image_url ? (
                              <img 
                                src={wrap.image_url}
                                alt={wrap.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-zinc-800" />
                            )}
                          </button>
                        ))}
                      </div>
                      {getFilteredWraps().length === 0 && (
                        <p className="text-xs text-zinc-600 text-center py-4">
                          No wraps found
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Generating State */}
            {generating && (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-6 relative">
                  <div className="absolute inset-0 border-2 animate-ping opacity-20" 
                       style={{ borderColor: accentColor }} />
                  <div className="absolute inset-0 border-2 border-t-transparent animate-spin"
                       style={{ borderColor: accentColor, borderTopColor: 'transparent' }} />
                </div>
                <p className="text-lg font-bold text-white mb-1">Generating your preview</p>
                <p className="font-mono text-xs tracking-widest text-zinc-500">30-60 SECONDS</p>
              </div>
            )}

            {/* Step 3: Result */}
            {step === 3 && resultImage && (
              <div className="text-center">
                <div className="relative mb-4">
                  <img 
                    src={resultImage} 
                    alt="Wrapped car preview" 
                    className="w-full max-w-xl mx-auto border border-zinc-800"
                  />
                  <div className="absolute top-2 right-2 px-2 py-1 text-[10px] font-mono tracking-wider"
                       style={{ backgroundColor: accentColor, color: '#000' }}>
                    AI PREVIEW
                  </div>
                </div>
                <p className="text-xl font-bold text-white mb-1">
                  Your car in <span style={{ color: accentColor }}>{selectedWrap?.name}</span>
                </p>
                <p className="text-zinc-500 text-sm mb-4">{selectedWrap?.finish} finish • {selectedWrap?.manufacturer}</p>
                <div className="flex justify-center gap-3 flex-wrap">
                  <button 
                    onClick={resetVisualizer}
                    className="border border-zinc-700 text-white font-medium text-sm px-5 py-2.5 hover:border-white transition-all"
                  >
                    Try Another
                  </button>
                  <button 
                    onClick={() => setShowInquireModal(true)}
                    className="font-bold text-sm px-5 py-2.5 transition-all hover:opacity-90 flex items-center gap-2"
                    style={{ backgroundColor: accentColor, color: '#000' }}
                  >
                    <MessageSquare className="w-4 h-4" />
                    Inquire About This Wrap
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Inquire Modal */}
      {showInquireModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#09090B] border border-zinc-800 max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-white mb-2">Contact {studioDetails?.name || 'Studio'}</h3>
            <p className="text-zinc-500 text-sm mb-6">
              Interested in <span style={{ color: accentColor }}>{selectedWrap?.name}</span>? Get in touch!
            </p>
            
            <div className="space-y-3 mb-6">
              {studioDetails?.phone && (
                <a 
                  href={`tel:${studioDetails.phone}`}
                  className="flex items-center gap-3 p-3 border border-zinc-800 hover:border-zinc-600 transition-colors"
                >
                  <Phone className="w-5 h-5" style={{ color: accentColor }} />
                  <span className="text-white">{studioDetails.phone}</span>
                </a>
              )}
              {studioDetails?.email && (
                <a 
                  href={`mailto:${studioDetails.email}?subject=Wrap Inquiry: ${selectedWrap?.name}&body=Hi, I'm interested in the ${selectedWrap?.name} wrap I previewed on your visualizer.`}
                  className="flex items-center gap-3 p-3 border border-zinc-800 hover:border-zinc-600 transition-colors"
                >
                  <Mail className="w-5 h-5" style={{ color: accentColor }} />
                  <span className="text-white">{studioDetails.email}</span>
                </a>
              )}
              {!studioDetails?.phone && !studioDetails?.email && (
                <Link to={`/s/${slug}/contact`} className="flex items-center gap-3 p-3 border border-zinc-800 hover:border-zinc-600 transition-colors">
                  <Mail className="w-5 h-5" style={{ color: accentColor }} />
                  <span className="text-white">Contact via form</span>
                </Link>
              )}
            </div>
            
            <button 
              onClick={() => setShowInquireModal(false)}
              className="w-full border border-zinc-700 text-zinc-400 py-2 hover:text-white hover:border-zinc-500 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

const StudioPage = () => {
  const { slug } = useParams();
  const [studio, setStudio] = useState(null);
  const [wraps, setWraps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStudio = async () => {
      try {
        const [studioRes, wrapsRes] = await Promise.all([
          axios.get(`${API}/studios/public/${slug}`),
          axios.get(`${API}/wraps/public/${slug}`)
        ]);
        setStudio(studioRes.data);
        setWraps(wrapsRes.data);
      } catch (err) {
        setError('Studio not found');
      } finally {
        setLoading(false);
      }
    };
    fetchStudio();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-zinc-800 border-t-[#FF3399] animate-spin" />
      </div>
    );
  }

  if (error || !studio) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-black mb-4 text-white">Studio Not Found</h1>
          <Link to="/" className="text-[#FF3399] hover:underline">Go back home</Link>
        </div>
      </div>
    );
  }

  const accentColor = studio.accent_color || '#FF3399';

  return (
    <div className="min-h-screen bg-[#050505] noise-overlay" data-testid="studio-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to={`/s/${slug}`} className="font-bold text-xl text-white flex items-center gap-3">
              {studio.logo_url ? (
                <img src={studio.logo_url} alt={studio.name} className="h-8" />
              ) : (
                <div className="w-8 h-8 flex items-center justify-center" style={{ backgroundColor: accentColor }}>
                  <span className="text-black font-black text-sm">{studio.name.charAt(0)}</span>
                </div>
              )}
              {studio.name}
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <a href="#visualizer" className="text-sm text-zinc-400 hover:text-white transition-colors">Visualizer</a>
              <a href="#about" className="text-sm text-zinc-400 hover:text-white transition-colors">About</a>
              <Link to={`/s/${slug}/contact`} className="text-sm text-zinc-400 hover:text-white transition-colors">Contact</Link>
            </div>
            <Link to={`/s/${slug}/contact`}>
              <button 
                className="btn-skew font-bold text-sm px-6 py-2 transition-all hover:opacity-90"
                style={{ backgroundColor: accentColor, color: '#000' }}
              >
                <span>Get Quote</span>
              </button>
            </Link>
          </div>
        </div>
      </nav>

      {/* VISUALIZER AT TOP */}
      <PremiumVisualizerWidget studioId={studio.id} slug={slug} studioDetails={studio} accentColor={accentColor} />

      {/* Studio Info Section - Below Visualizer */}
      <section className="py-16 px-6 lg:px-12 border-t border-zinc-800" id="about">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-white mb-4">
              {studio.name}
            </h1>
            <p className="text-lg text-zinc-400 mb-6 max-w-2xl mx-auto">{studio.description}</p>
            
            {studio.address && (
              <p className="flex items-center justify-center gap-2 text-zinc-500 mb-8">
                <MapPin size={18} style={{ color: accentColor }} />
                {studio.address}
              </p>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#visualizer">
                <button 
                  className="btn-skew font-bold uppercase tracking-wider px-8 py-4 transition-all hover:opacity-90"
                  style={{ backgroundColor: accentColor, color: '#000' }}
                >
                  <span className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    Try Visualizer
                  </span>
                </button>
              </a>
              <Link to={`/s/${slug}/contact`}>
                <button className="btn-skew border border-zinc-700 text-white font-medium uppercase tracking-wider px-8 py-4 hover:border-white transition-all">
                  <span>Get a Quote</span>
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      {studio.services?.length > 0 && (
        <section className="py-16 px-6 lg:px-12 bg-zinc-900/30">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <p className="font-mono text-xs tracking-widest mb-3" style={{ color: accentColor }}>WHAT WE DO</p>
              <h2 className="text-3xl font-black tracking-tighter text-white">Our Services</h2>
            </div>
            
            <div className="flex flex-wrap justify-center gap-3">
              {studio.services.map((service, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="card-glow bg-zinc-900/50 border border-zinc-800 p-5 text-center min-w-[150px]"
                >
                  <p className="font-medium text-zinc-300 text-sm">{service}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Manufacturers Section */}
      <section className="py-16 px-6 lg:px-12 border-y border-zinc-800">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="font-mono text-xs tracking-widest text-zinc-500 mb-3">PREMIUM MATERIALS</p>
            <h2 className="text-2xl font-black tracking-tighter text-white">Wraps from top manufacturers</h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {['3M', 'Avery Dennison', 'KPMF', 'Oracal', 'Arlon'].map((name, i) => (
              <div key={i} className="text-center p-4 bg-zinc-900/30 border border-zinc-800">
                <span className="text-lg font-bold text-zinc-400">{name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      {studio.gallery_images?.length > 1 && (
        <section className="py-16 px-6 lg:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <p className="font-mono text-xs tracking-widest mb-3" style={{ color: accentColor }}>PORTFOLIO</p>
              <h2 className="text-3xl font-black tracking-tighter text-white">Our Work</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {studio.gallery_images.slice(0, 6).map((img, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="aspect-video bg-zinc-900 overflow-hidden border border-zinc-800 card-glow"
                >
                  <img 
                    src={img} 
                    alt={`Gallery ${i + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      {studio.testimonials?.length > 0 && (
        <section className="py-16 px-6 lg:px-12 bg-zinc-900/30">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <p className="font-mono text-xs tracking-widest mb-3" style={{ color: accentColor }}>TESTIMONIALS</p>
              <h2 className="text-3xl font-black tracking-tighter text-white">What Clients Say</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {studio.testimonials.slice(0, 4).map((testimonial, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="card-glow bg-zinc-900/50 border border-zinc-800 p-6"
                >
                  <div className="flex gap-1 mb-3">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} size={12} fill={accentColor} color={accentColor} />
                    ))}
                  </div>
                  <p className="text-zinc-400 mb-4 text-sm italic">"{testimonial.text}"</p>
                  <p className="font-bold text-white text-sm">{testimonial.name}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 px-6 lg:px-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30"
             style={{ background: `radial-gradient(ellipse at center, ${accentColor}20 0%, transparent 70%)` }} />
        
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-white mb-4">
            Ready to transform<br />
            <span style={{ color: accentColor }}>your vehicle?</span>
          </h2>
          <p className="text-zinc-400 mb-8">Get a free consultation and quote from our expert team.</p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <a href="#visualizer">
              <button 
                className="btn-skew font-bold uppercase tracking-wider px-10 py-5 transition-all hover:opacity-90"
                style={{ backgroundColor: accentColor, color: '#000' }}
              >
                <span className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  Try Visualizer
                </span>
              </button>
            </a>
            <Link to={`/s/${slug}/contact`}>
              <button className="btn-skew border border-zinc-700 text-white font-medium uppercase tracking-wider px-10 py-5 hover:border-white transition-all">
                <span>Contact Us</span>
              </button>
            </Link>
          </div>

          <div className="flex flex-wrap justify-center gap-6 text-zinc-500 text-sm">
            {studio.phone && (
              <a href={`tel:${studio.phone}`} className="flex items-center gap-2 hover:text-white transition-colors">
                <Phone size={16} /> {studio.phone}
              </a>
            )}
            {studio.email && (
              <a href={`mailto:${studio.email}`} className="flex items-center gap-2 hover:text-white transition-colors">
                <Mail size={16} /> {studio.email}
              </a>
            )}
          </div>

          {Object.keys(studio.social_links || {}).length > 0 && (
            <div className="flex justify-center gap-4 mt-6">
              {studio.social_links.instagram && (
                <a href={studio.social_links.instagram} target="_blank" rel="noopener noreferrer" 
                   className="w-10 h-10 border border-zinc-800 flex items-center justify-center text-zinc-500 hover:text-white hover:border-zinc-600 transition-colors">
                  <Instagram size={18} />
                </a>
              )}
              {studio.social_links.facebook && (
                <a href={studio.social_links.facebook} target="_blank" rel="noopener noreferrer"
                   className="w-10 h-10 border border-zinc-800 flex items-center justify-center text-zinc-500 hover:text-white hover:border-zinc-600 transition-colors">
                  <Facebook size={18} />
                </a>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-zinc-600 text-sm">
            © {new Date().getFullYear()} {studio.name}
          </p>
          <p className="font-mono text-xs text-zinc-600">
            Powered by <Link to="/" className="text-zinc-500 hover:text-white transition-colors">WrapViz</Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default StudioPage;
